import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BottomTabs } from "@/components/BottomTabs";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PinPad } from "@/components/PinPad";
import { toast } from "sonner";
import { Loader2, Youtube, Music2, Instagram, Sparkles, Copy, ExternalLink, Lock, ShieldCheck, Store, Clock } from "lucide-react";
import { HustleHistorySection } from "@/components/HustleHistorySection";

import { NotificationPrefs } from "@/components/NotificationPrefs";
import { UpgradeToPro } from "@/components/UpgradeToPro";
import { PowerTranzStatusTracker } from "@/components/PowerTranzStatusTracker";

type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

interface DayHours {
  open: string;
  close: string;
  closed: boolean;
}
type BusinessHours = Record<DayKey, DayHours>;

const DEFAULT_HOURS: BusinessHours = {
  mon: { open: "09:00", close: "17:00", closed: false },
  tue: { open: "09:00", close: "17:00", closed: false },
  wed: { open: "09:00", close: "17:00", closed: false },
  thu: { open: "09:00", close: "17:00", closed: false },
  fri: { open: "09:00", close: "17:00", closed: false },
  sat: { open: "10:00", close: "14:00", closed: false },
  sun: { open: "10:00", close: "14:00", closed: true },
};

interface ProfileForm {
  display_name: string;
  cashtag: string;
  is_creator: boolean;
  is_merchant: boolean;
  bio: string;
  youtube_url: string;
  spotify_url: string;
  tiktok_url: string;
  instagram_url: string;
  store_name: string;
  show_cashtag_publicly: boolean;
  business_hours: BusinessHours;
}

const empty: ProfileForm = {
  display_name: "",
  cashtag: "",
  is_creator: false,
  is_merchant: false,
  bio: "",
  youtube_url: "",
  spotify_url: "",
  tiktok_url: "",
  instagram_url: "",
  store_name: "",
  show_cashtag_publicly: true,
  business_hours: DEFAULT_HOURS,
};

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState<ProfileForm>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasPinSet, setHasPinSet] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, cashtag, is_creator, is_merchant, bio, youtube_url, spotify_url, tiktok_url, instagram_url, store_name, show_cashtag_publicly, business_hours")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          const d = data as any;
          setForm({
            display_name: d.display_name ?? "",
            cashtag: d.cashtag ?? "",
            is_creator: d.is_creator ?? false,
            is_merchant: d.is_merchant ?? false,
            bio: d.bio ?? "",
            youtube_url: d.youtube_url ?? "",
            spotify_url: d.spotify_url ?? "",
            tiktok_url: d.tiktok_url ?? "",
            instagram_url: d.instagram_url ?? "",
            store_name: d.store_name ?? "",
            show_cashtag_publicly: d.show_cashtag_publicly ?? true,
            business_hours: { ...DEFAULT_HOURS, ...(d.business_hours ?? {}) },
          });
        }
        setLoading(false);
      });
    supabase.rpc("has_pin").then(({ data }) => setHasPinSet(Boolean(data)));
    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [user]);

  const refreshHasPin = () =>
    supabase.rpc("has_pin").then(({ data }) => setHasPinSet(Boolean(data)));

  const update = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        display_name: form.display_name.trim(),
        is_creator: form.is_creator,
        // is_merchant is set via admin review of merchant_applications, not editable here
        bio: form.bio.trim() || null,
        youtube_url: form.youtube_url.trim() || null,
        spotify_url: form.spotify_url.trim() || null,
        tiktok_url: form.tiktok_url.trim() || null,
        instagram_url: form.instagram_url.trim() || null,
        store_name: form.store_name.trim().slice(0, 60) || null,
        show_cashtag_publicly: form.show_cashtag_publicly,
        business_hours: form.business_hours as any,
      })
      .eq("user_id", user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Profile saved 🌴");
  };

  const publicUrl = `${window.location.origin}/u/${form.cashtag}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Public link copied");
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Profile & Settings</h1>
      </BrandHeader>

      {loading ? (
        <div className="flex-1 grid place-items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <form onSubmit={handleSave} className="px-5 py-6 space-y-6 flex-1 animate-fade-in-up">
          <div className="space-y-2">
            <Label htmlFor="display_name">Display name</Label>
            <Input
              id="display_name"
              value={form.display_name}
              onChange={(e) => update("display_name", e.target.value)}
              className="h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
            />
          </div>

          <div className="space-y-2">
            <Label>Cashtag</Label>
            <div className="h-12 rounded-xl bg-muted/60 px-4 flex items-center text-muted-foreground font-medium">
              ${form.cashtag}
            </div>
          </div>

          {/* Hustle Score history */}
          <HustleHistorySection />

          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="w-full rounded-2xl border border-border bg-card p-5 flex items-center justify-between hover:bg-muted/40 transition"
            >
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div className="text-left">
                  <p className="font-display font-bold text-sm">Admin Console</p>
                  <p className="text-[11px] text-muted-foreground">Dashboard, referrals, audit log & more</p>
                </div>
              </div>
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
            </button>
          )}

          

          {/* Wallet PIN */}
          <PinSection hasPin={hasPinSet} onChanged={refreshHasPin} />

          {/* Notification preferences */}
          <NotificationPrefs />

          {/* Premium / Upgrade */}
          <UpgradeToPro />

          {/* PowerTranz payment status */}
          <PowerTranzStatusTracker pendingOnly={false} limit={5} />





          {/* Merchant mode */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-primary/10 p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Store className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">Merchant mode</h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.is_merchant
                    ? "You're approved as a merchant. Open your merchant home for live earnings & QR."
                    : "Apply to accept payments at your business. Applications are reviewed by our team."}
                </p>
              </div>
            </div>
            {form.is_merchant ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3 w-full"
                onClick={() => navigate("/merchant")}
              >
                Open Merchant home →
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                className="mt-3 w-full"
                onClick={() => navigate("/merchant/apply")}
              >
                Apply to become a merchant →
              </Button>
            )}
          </div>

          {/* Merchant profile details */}
          {form.is_merchant && (
            <div className="rounded-2xl border border-border bg-card p-5 space-y-5 animate-fade-in-up">
              <div className="flex items-center gap-2">
                <Store className="h-4 w-4 text-primary" />
                <h3 className="font-display font-bold">Merchant profile</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="store_name">Store name</Label>
                <Input
                  id="store_name"
                  value={form.store_name}
                  onChange={(e) => update("store_name", e.target.value.slice(0, 60))}
                  maxLength={60}
                  placeholder="e.g. Sunset Coffee Co."
                  className="h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {form.store_name.length}/60 · shown on your QR screen
                </p>
              </div>

              <div className="flex items-start justify-between gap-4 rounded-xl bg-secondary/40 p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium">Show ${form.cashtag} publicly</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Display your cashtag under the QR so customers can pay manually.
                  </p>
                </div>
                <Switch
                  checked={form.show_cashtag_publicly}
                  onCheckedChange={(v) => update("show_cashtag_publicly", v)}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <h4 className="font-display font-bold text-sm">Business hours</h4>
                </div>
                <div className="space-y-2">
                  {DAYS.map(({ key, label }) => {
                    const h = form.business_hours[key];
                    return (
                      <div
                        key={key}
                        className="flex items-center gap-2 rounded-xl bg-secondary/40 px-3 py-2"
                      >
                        <span className="w-10 text-sm font-medium">{label}</span>
                        {h.closed ? (
                          <span className="flex-1 text-sm text-muted-foreground">Closed</span>
                        ) : (
                          <div className="flex-1 flex items-center gap-2">
                            <Input
                              type="time"
                              value={h.open}
                              onChange={(e) =>
                                update("business_hours", {
                                  ...form.business_hours,
                                  [key]: { ...h, open: e.target.value },
                                })
                              }
                              className="h-9 rounded-lg bg-background/80 border-border text-sm px-2"
                            />
                            <span className="text-xs text-muted-foreground">–</span>
                            <Input
                              type="time"
                              value={h.close}
                              onChange={(e) =>
                                update("business_hours", {
                                  ...form.business_hours,
                                  [key]: { ...h, close: e.target.value },
                                })
                              }
                              className="h-9 rounded-lg bg-background/80 border-border text-sm px-2"
                            />
                          </div>
                        )}
                        <Switch
                          checked={!h.closed}
                          onCheckedChange={(v) =>
                            update("business_hours", {
                              ...form.business_hours,
                              [key]: { ...h, closed: !v },
                            })
                          }
                          aria-label={`${label} open`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Creator mode toggle */}
          <div className="rounded-2xl border border-border bg-gradient-to-br from-accent/10 to-primary/5 p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-accent-foreground" />
                  <h3 className="font-display font-bold">Creator mode</h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Publish a public profile and accept tips from anyone via your $cashtag link.
                </p>
              </div>
              <Switch
                checked={form.is_creator}
                onCheckedChange={(v) => update("is_creator", v)}
              />
            </div>

            {form.is_creator && (
              <div className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-2 text-xs">
                <span className="truncate flex-1 text-muted-foreground">{publicUrl}</span>
                <button type="button" onClick={copyLink} className="text-primary p-1" aria-label="Copy link">
                  <Copy className="h-4 w-4" />
                </button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary p-1"
                  aria-label="Open public profile"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={form.bio}
              onChange={(e) => update("bio", e.target.value)}
              maxLength={240}
              rows={3}
              placeholder="Tell the islands what you do…"
              className="rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary resize-none"
            />
            <p className="text-xs text-muted-foreground text-right">{form.bio.length}/240</p>
          </div>

          <div className="space-y-3">
            <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground">
              Social links
            </h3>
            <SocialField
              icon={<Youtube className="h-5 w-5 text-[hsl(0_84%_55%)]" />}
              id="youtube_url"
              label="YouTube"
              placeholder="https://youtube.com/@you"
              value={form.youtube_url}
              onChange={(v) => update("youtube_url", v)}
            />
            <SocialField
              icon={<Music2 className="h-5 w-5 text-[hsl(141_76%_36%)]" />}
              id="spotify_url"
              label="Spotify"
              placeholder="https://open.spotify.com/artist/…"
              value={form.spotify_url}
              onChange={(v) => update("spotify_url", v)}
            />
            <SocialField
              icon={<TikTokIcon />}
              id="tiktok_url"
              label="TikTok"
              placeholder="https://tiktok.com/@you"
              value={form.tiktok_url}
              onChange={(v) => update("tiktok_url", v)}
            />
            <SocialField
              icon={<Instagram className="h-5 w-5 text-[hsl(330_81%_60%)]" />}
              id="instagram_url"
              label="Instagram"
              placeholder="https://instagram.com/you"
              value={form.instagram_url}
              onChange={(v) => update("instagram_url", v)}
            />
          </div>

          <Button type="submit" variant="hero" size="xl" className="w-full" disabled={saving}>
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
          </Button>
        </form>
      )}

      <div className="px-1 pt-4">
        <button
          type="button"
          onClick={() => navigate("/settings/delete-account")}
          className="w-full text-left text-sm text-destructive hover:underline"
        >
          Delete my account
        </button>
      </div>

      <BottomTabs />
    </MobileShell>
  );
};

const SocialField = ({
  icon,
  id,
  label,
  placeholder,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <Label htmlFor={id} className="sr-only">{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2">{icon}</span>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type="url"
        className="pl-11 h-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
      />
    </div>
  </div>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-foreground" aria-hidden="true">
    <path d="M19.5 6.6a5.4 5.4 0 0 1-3.2-1V15a5.6 5.6 0 1 1-5.6-5.6c.3 0 .6 0 .9.1v2.7a2.9 2.9 0 1 0 2 2.8V2h2.6a5.4 5.4 0 0 0 3.3 4.6v0Z" />
  </svg>
);

const PinSection = ({ hasPin, onChanged }: { hasPin: boolean | null; onChanged: () => void }) => {
  const [mode, setMode] = useState<"idle" | "set" | "change">("idle");
  const [step, setStep] = useState<"current" | "new" | "confirm">("new");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setMode("idle");
    setStep(hasPin ? "current" : "new");
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
  };

  const start = (m: "set" | "change") => {
    setMode(m);
    setStep(m === "change" ? "current" : "new");
    setCurrentPin("");
    setNewPin("");
    setConfirmPin("");
  };

  const submit = async () => {
    if (newPin.length !== 4 || confirmPin.length !== 4) return;
    if (newPin !== confirmPin) {
      toast.error("PINs don't match");
      setConfirmPin("");
      setStep("confirm");
      return;
    }
    setSaving(true);
    const { error } = await supabase.rpc("set_pin", {
      new_pin: newPin,
      current_pin: mode === "change" ? currentPin : null,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(hasPin ? "PIN updated 🔒" : "PIN set 🔒");
    onChanged();
    reset();
  };

  // Auto-advance steps as user fills digits
  useEffect(() => {
    if (mode === "change" && step === "current" && currentPin.length === 4) setStep("new");
  }, [currentPin, mode, step]);
  useEffect(() => {
    if (mode !== "idle" && step === "new" && newPin.length === 4) setStep("confirm");
  }, [newPin, step, mode]);
  useEffect(() => {
    if (mode !== "idle" && step === "confirm" && confirmPin.length === 4) submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmPin]);

  if (mode === "idle") {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-4">
        <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
          {hasPin ? <ShieldCheck className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold">Wallet PIN</h3>
          <p className="text-xs text-muted-foreground mt-1">
            {hasPin
              ? "Required for every send. Locks for 15 minutes after 5 wrong tries."
              : "Set a 4-digit PIN to enable sending money."}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant={hasPin ? "outline" : "hero"}
          onClick={() => start(hasPin ? "change" : "set")}
        >
          {hasPin ? "Change" : "Set PIN"}
        </Button>
      </div>
    );
  }

  const titles: Record<typeof step, string> = {
    current: "Enter current PIN",
    new: "Choose a 4-digit PIN",
    confirm: "Confirm new PIN",
  };
  const value = step === "current" ? currentPin : step === "new" ? newPin : confirmPin;
  const setValue = step === "current" ? setCurrentPin : step === "new" ? setNewPin : setConfirmPin;

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold">{titles[step]}</h3>
        <button
          type="button"
          onClick={reset}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
      <PinPad value={value} onChange={setValue} disabled={saving} />
      {saving && (
        <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Saving…
        </p>
      )}
    </div>
  );
};

export default Settings;
